import { SpeechSegment } from '../../../services/api.service';
import {
  formatSecondsShort,
  formatSpeakerShortName,
  getSegmentEndSec,
  getSegmentStartSec,
  getSpeakerName,
} from './audio-segments';

const QUESTION_RE =
  /\?|(?:^|[\s,.!])(?:как|что|где|когда|почему|зачем|кто|какой|какая|какие|сколько|можно\s+ли|не\s+могли\s+бы|есть\s+ли|разве)\b/i;

const LANG_LABELS: Record<string, string> = {
  ru: 'Русский',
  kz: 'Казахский',
  kk: 'Казахский',
  en: 'English',
  mixed: 'Смешанный',
  unknown: 'Не определён',
};

export type MonologueStat = {
  speaker: string;
  displayName: string;
  color: string;
  startSec: number;
  durationSec: number;
  durationLabel: string;
  replicaCount: number;
};

export type OverlapStat = {
  atSec: number;
  timeLabel: string;
  speakerA: string;
  speakerB: string;
  colorA: string;
  colorB: string;
  overlapSec: number;
};

export type DeadZone = {
  startSec: number;
  endSec: number;
  durationSec: number;
  durationLabel: string;
};

export type AdvancedMeetingMetrics = {
  gini: number;
  giniLabel: string;
  monologues: MonologueStat[];
  overlaps: { total: number; top: OverlapStat[] };
  questions: {
    total: number;
    replicaCount: number;
    ratePct: number;
    bySpeaker: { speaker: string; displayName: string; color: string; count: number; ratePct: number }[];
  };
  speechRate: {
    overallWpm: number;
    bySpeaker: { speaker: string; displayName: string; color: string; wpm: number; words: number }[];
  };
  deadZones: DeadZone[];
  languageMix: { lang: string; label: string; pct: number; seconds: number }[];
  languageBySpeaker: {
    speaker: string;
    displayName: string;
    color: string;
    dominantLang: string;
    dominantPct: number;
  }[];
};

function countWords(text: string): number {
  return (text.match(/\S+/g) || []).length;
}

function giniCoefficient(values: number[]): number {
  const cleaned = values.filter(v => v > 0);
  if (!cleaned.length) return 0;
  const total = cleaned.reduce((a, b) => a + b, 0);
  if (total <= 0) return 0;
  const sorted = [...cleaned].sort((a, b) => a - b);
  const n = sorted.length;
  const weighted = sorted.reduce((sum, v, i) => sum + (i + 1) * v, 0);
  return Math.round(((2 * weighted) / (n * total) - (n + 1) / n) * 100) / 100;
}

function giniLabel(gini: number): string {
  if (gini < 0.25) return 'равномерно';
  if (gini < 0.45) return 'умеренный дисбаланс';
  return 'сильный дисбаланс';
}

export function buildAdvancedMetrics(
  segments: SpeechSegment[],
  durationSec: number,
  speakerColors: Record<string, string>,
  monologueMinSec = 18,
  deadZoneMinSec = 25,
): AdvancedMeetingMetrics | null {
  const sorted = [...segments]
    .filter(s => (s.text || '').trim())
    .sort((a, b) => getSegmentStartSec(a) - getSegmentStartSec(b));
  if (!sorted.length) return null;

  const color = (name: string) => speakerColors[name] || '#a3a3a3';

  const monologues: MonologueStat[] = [];
  let blockName = getSpeakerName(sorted[0]);
  let blockStart = getSegmentStartSec(sorted[0]);
  let blockEnd = getSegmentEndSec(sorted[0]);
  let blockCount = 1;

  const pushMonologue = () => {
    const dur = blockEnd - blockStart;
    if (dur >= monologueMinSec) {
      monologues.push({
        speaker: blockName,
        displayName: formatSpeakerShortName(blockName),
        color: color(blockName),
        startSec: blockStart,
        durationSec: Math.round(dur * 10) / 10,
        durationLabel: formatSecondsShort(dur),
        replicaCount: blockCount,
      });
    }
  };

  for (let i = 1; i < sorted.length; i++) {
    const seg = sorted[i];
    const name = getSpeakerName(seg);
    const start = getSegmentStartSec(seg);
    const end = getSegmentEndSec(seg);
    if (name === blockName && start - blockEnd <= 2) {
      blockEnd = Math.max(blockEnd, end);
      blockCount += 1;
    } else {
      pushMonologue();
      blockName = name;
      blockStart = start;
      blockEnd = end;
      blockCount = 1;
    }
  }
  pushMonologue();
  monologues.sort((a, b) => b.durationSec - a.durationSec);

  const overlaps: OverlapStat[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    const prevEnd = getSegmentEndSec(prev);
    const currStart = getSegmentStartSec(curr);
    if (currStart < prevEnd - 0.05) {
      const speakerA = getSpeakerName(prev);
      const speakerB = getSpeakerName(curr);
      overlaps.push({
        atSec: currStart,
        timeLabel: formatSecondsShort(currStart),
        speakerA,
        speakerB,
        colorA: color(speakerA),
        colorB: color(speakerB),
        overlapSec: Math.round((prevEnd - currStart) * 10) / 10,
      });
    }
  }
  overlaps.sort((a, b) => b.overlapSec - a.overlapSec);

  const replicaBySpeaker = new Map<string, number>();
  const questionBySpeaker = new Map<string, number>();
  let totalQuestions = 0;
  let totalReplicas = 0;

  sorted.forEach(seg => {
    const text = seg.text?.trim() || '';
    if (!text) return;
    const name = getSpeakerName(seg);
    replicaBySpeaker.set(name, (replicaBySpeaker.get(name) || 0) + 1);
    totalReplicas += 1;
    if (QUESTION_RE.test(text)) {
      totalQuestions += 1;
      questionBySpeaker.set(name, (questionBySpeaker.get(name) || 0) + 1);
    }
  });

  const questionsBySpeaker = Array.from(questionBySpeaker.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([speaker, count]) => {
      const reps = replicaBySpeaker.get(speaker) || 1;
      return {
        speaker,
        displayName: formatSpeakerShortName(speaker),
        color: color(speaker),
        count,
        ratePct: Math.round((count / reps) * 100),
      };
    });

  const wordsBySpeaker = new Map<string, number>();
  const secBySpeaker = new Map<string, number>();
  sorted.forEach(seg => {
    const text = seg.text?.trim() || '';
    if (!text) return;
    const name = getSpeakerName(seg);
    wordsBySpeaker.set(name, (wordsBySpeaker.get(name) || 0) + countWords(text));
    secBySpeaker.set(name, (secBySpeaker.get(name) || 0) + Math.max(0.1, getSegmentEndSec(seg) - getSegmentStartSec(seg)));
  });

  const speechRateBySpeaker = Array.from(wordsBySpeaker.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([speaker, words]) => {
      const sec = secBySpeaker.get(speaker) || 1;
      return {
        speaker,
        displayName: formatSpeakerShortName(speaker),
        color: color(speaker),
        wpm: Math.round(words / (sec / 60)),
        words,
      };
    });

  const totalWords = Array.from(wordsBySpeaker.values()).reduce((a, b) => a + b, 0);
  const totalSpeechSec = Array.from(secBySpeaker.values()).reduce((a, b) => a + b, 0) || 1;
  const overallWpm = Math.round(totalWords / (totalSpeechSec / 60));

  const deadZones: DeadZone[] = [];
  const firstStart = getSegmentStartSec(sorted[0]);
  if (firstStart >= deadZoneMinSec) {
    deadZones.push({
      startSec: 0,
      endSec: firstStart,
      durationSec: Math.round(firstStart * 10) / 10,
      durationLabel: formatSecondsShort(firstStart),
    });
  }
  for (let i = 0; i < sorted.length - 1; i++) {
    const gapStart = getSegmentEndSec(sorted[i]);
    const gapEnd = getSegmentStartSec(sorted[i + 1]);
    const gap = gapEnd - gapStart;
    if (gap >= deadZoneMinSec) {
      deadZones.push({
        startSec: gapStart,
        endSec: gapEnd,
        durationSec: Math.round(gap * 10) / 10,
        durationLabel: formatSecondsShort(gap),
      });
    }
  }
  const lastEnd = getSegmentEndSec(sorted[sorted.length - 1]);
  const tail = durationSec - lastEnd;
  if (tail >= deadZoneMinSec) {
    deadZones.push({
      startSec: lastEnd,
      endSec: durationSec,
      durationSec: Math.round(tail * 10) / 10,
      durationLabel: formatSecondsShort(tail),
    });
  }
  deadZones.sort((a, b) => b.durationSec - a.durationSec);

  const gini = giniCoefficient(Array.from(secBySpeaker.values()));

  const langSec = new Map<string, number>();
  const langBySpeaker = new Map<string, Map<string, number>>();
  sorted.forEach(seg => {
    const text = seg.text?.trim() || '';
    if (!text) return;
    let lang = (seg.speaker_language || '').toLowerCase().trim() || 'unknown';
    if (lang === 'kk') lang = 'kz';
    const dur = Math.max(0.1, getSegmentEndSec(seg) - getSegmentStartSec(seg));
    langSec.set(lang, (langSec.get(lang) || 0) + dur);
    const name = getSpeakerName(seg);
    if (!langBySpeaker.has(name)) langBySpeaker.set(name, new Map());
    const m = langBySpeaker.get(name)!;
    m.set(lang, (m.get(lang) || 0) + dur);
  });

  const langTotal = Array.from(langSec.values()).reduce((a, b) => a + b, 0) || 1;
  const languageMix = Array.from(langSec.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([lang, seconds]) => ({
      lang,
      label: LANG_LABELS[lang] || lang.toUpperCase(),
      pct: Math.round((seconds / langTotal) * 100),
      seconds: Math.round(seconds * 10) / 10,
    }));

  const languageBySpeaker = Array.from(langBySpeaker.entries())
    .slice(0, 8)
    .map(([speaker, langs]) => {
      const total = Array.from(langs.values()).reduce((a, b) => a + b, 0) || 1;
      const dominant = Array.from(langs.entries()).sort((a, b) => b[1] - a[1])[0];
      return {
        speaker,
        displayName: formatSpeakerShortName(speaker),
        color: color(speaker),
        dominantLang: LANG_LABELS[dominant[0]] || dominant[0],
        dominantPct: Math.round((dominant[1] / total) * 100),
      };
    });

  return {
    gini,
    giniLabel: giniLabel(gini),
    monologues: monologues.slice(0, 6),
    overlaps: { total: overlaps.length, top: overlaps.slice(0, 8) },
    questions: {
      total: totalQuestions,
      replicaCount: totalReplicas,
      ratePct: totalReplicas ? Math.round((totalQuestions / totalReplicas) * 100) : 0,
      bySpeaker: questionsBySpeaker,
    },
    speechRate: { overallWpm, bySpeaker: speechRateBySpeaker },
    deadZones: deadZones.slice(0, 6),
    languageMix,
    languageBySpeaker,
  };
}
